import { useCallback } from "react";

import { usePagesStore } from "@/stores/pages-store";
import { visualEditorService } from "@/lib/visual-editor/visual-editor-service";
import type {
  CreatePageResponse,
  DeletePageResponse,
  RenamePageResponse,
  DuplicatePageResponse,
  SetAsHomepageResponse,
  PageInfo,
} from "@/types/pages.types";

interface UsePagesReturn {
  pages: PageInfo[];
  isLoading: boolean;
  error: string | null;
  fetchPages: () => Promise<void>;
  createPage: (routeName: string) => Promise<CreatePageResponse>;
  deletePage: (route: string) => Promise<DeletePageResponse>;
  renamePage: (
    oldRoute: string,
    newRouteName: string,
  ) => Promise<RenamePageResponse>;
  duplicatePage: (sourceRoute: string) => Promise<DuplicatePageResponse>;
  setAsHomepage: (route: string) => Promise<SetAsHomepageResponse>;
}

export function usePages(devContainerId: string | undefined): UsePagesReturn {
  const { pages, isLoading, error, setPages, setLoading, setError } =
    usePagesStore();

  const fetchPages = useCallback(async (): Promise<void> => {
    if (!devContainerId) return;

    setLoading(true);
    setError(null);

    const result = await visualEditorService.getPages(devContainerId);
    if (result.success) {
      setPages(result.pages);
    } else {
      setError("Failed to load pages");
    }
    setLoading(false);
  }, [devContainerId, setPages, setLoading, setError]);

  const createPage = useCallback(
    async (routeName: string): Promise<CreatePageResponse> => {
      if (!devContainerId) {
        return {
          success: false,
          route: "",
          filePath: "",
          registered: false,
          error: "No dev container ID",
        };
      }

      const result = await visualEditorService.createPage(routeName, devContainerId);

      if (result.success) {
        await fetchPages();
      }

      return result;
    },
    [devContainerId, fetchPages],
  );

  const deletePage = useCallback(
    async (route: string): Promise<DeletePageResponse> => {
      if (!devContainerId) {
        return { success: false, error: "No dev container ID" };
      }

      const result = await visualEditorService.deletePage(route, devContainerId);

      if (result.success) {
        await fetchPages();
      }

      return result;
    },
    [devContainerId, fetchPages],
  );

  const renamePage = useCallback(
    async (
      oldRoute: string,
      newRouteName: string,
    ): Promise<RenamePageResponse> => {
      if (!devContainerId) {
        return { success: false, error: "No dev container ID" };
      }

      const result = await visualEditorService.renamePage(
        oldRoute,
        newRouteName,
        devContainerId,
      );

      if (result.success) {
        await fetchPages();
      }

      return result;
    },
    [devContainerId, fetchPages],
  );

  const duplicatePage = useCallback(
    async (sourceRoute: string): Promise<DuplicatePageResponse> => {
      if (!devContainerId) {
        return {
          success: false,
          route: "",
          filePath: "",
          registered: false,
          error: "No dev container ID",
        };
      }

      const result = await visualEditorService.duplicatePage(sourceRoute, devContainerId);

      if (result.success) {
        await fetchPages();
      }

      return result;
    },
    [devContainerId, fetchPages],
  );

  const setAsHomepage = useCallback(
    async (route: string): Promise<SetAsHomepageResponse> => {
      if (!devContainerId) {
        return { success: false, error: "No dev container ID" };
      }

      const result = await visualEditorService.setAsHomepage(route, devContainerId);

      if (result.success) {
        await fetchPages();
      }

      return result;
    },
    [devContainerId, fetchPages],
  );

  return {
    pages,
    isLoading,
    error,
    fetchPages,
    createPage,
    deletePage,
    renamePage,
    duplicatePage,
    setAsHomepage,
  };
}
